/**
 * Корейский возраст по дню рождения (на сегодня или на дату)
 * ====================================
 *
 * TODO Extract translations
 * TODO Ссылка на результат
 *
 * URLs:
 * - korean-age (index)
 * - korean-age/if-born-january-15-2024
 * - korean-age/at-january-15-2025-if-born-january-15-2024
 *
 * Калькулятор требует настройки robots.txt, чтобы не индексировать бессмысленно страницы с конкретными датами:
 * Allow: /ru/dates/korean-age$
 * Disallow: /ru/dates/korean-age/
 *
 * Почему не ранжируется по : корейский возраст +чем отличается 300
 * TODO:
 *  H2 - Legal Age in Korea
 *  H2 - Legal Drinking Age in Korea
 *  H2 - Military conscription age in Korea
 *  pages for how old is 16 (XX) in korean age
 *
 * TODO Optimize image alt and caption: https://seosherpa.com/image-seo/
 * TODO Add image to sitemap!!!!
 * TODO Add open graph
 */
import { LoaderFunctionArgs, MetaFunction, useLoaderData, useLocation } from "react-router";
import { TFunction } from "i18next";
import { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import ArticleImage from "~/components/ArticleImage";
import BlockQuote from "~/components/BlockQuote";
import Button from "~/components/Button";
import DatePicker from "~/components/forms/DatePicker";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import Icon from "~/components/Icon";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Author from "~/components/tool-page/Author";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { formatLongDate } from "~/helpers/dates";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, createFAQSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";


const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");
const CATEGORY = "dates";
const TKEY = "calculators.dates.korean_age";
const ROUTE_ENDING = "korean-age";


export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const result = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      data?.meta.images as string[],
      LAST_UPDATED
    )
  ];
  const faqSchema = createFAQSchema(data?.faq ?? []);
  if (faqSchema) result.push(faqSchema);
  return result;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};


interface Query {
  birthday: Date;
  at: Date | null;
}

class InvalidQueryError extends Error {
}

function parseInput(input: string): Query {
  let atDayStr: string | null = null;
  let atMonthName: string | null = null;
  let atYearStr: string | null = null;
  let birthDayStr: string | null = null;
  let birthMonthName: string | null = null;
  let birthYearStr: string | null = null;

  if (input.startsWith("at-")) {
    const regex = /^at-(\w+)-(\d+)-(\d{4})-if-born-(\w+)-(\d+)-(\d{4})$/;
    const match = input.match(regex);

    if (!match) {
      console.error("Invalid input format");
      throw new InvalidQueryError();
    }

    atDayStr = match[2];
    atMonthName = match[1];
    atYearStr = match[3];
    birthDayStr = match[5];
    birthMonthName = match[4];
    birthYearStr = match[6];
  } else {
    const regex = /^if-born-(\w+)-(\d+)-(\d{4})$/;
    const match = input.match(regex);

    if (!match) {
      console.error("Invalid input format");
      throw new InvalidQueryError();
    }

    birthDayStr = match[2];
    birthMonthName = match[1];
    birthYearStr = match[3];
  }

  const birthDay = parseInt(birthDayStr, 10);
  const birthMonth = new Date(Date.parse(`01 ${birthMonthName} 2000`)).getMonth() + 1;
  const birthYear = parseInt(birthYearStr, 10);

  const birthday = new Date(birthYear, birthMonth - 1, birthDay);
  if (isNaN(birthday.getTime())) {
    console.error("Invalid date");
    throw new InvalidQueryError();
  }

  if (atDayStr !== null && atMonthName !== null && atYearStr !== null) {
    const atDay = parseInt(atDayStr, 10);
    const atMonth = new Date(Date.parse(`01 ${atMonthName} 2000`)).getMonth() + 1;
    const atYear = parseInt(atYearStr, 10);

    const at = new Date(atYear, atMonth - 1, atDay);
    if (isNaN(at.getTime())) {
      console.error("Invalid date");
      throw new InvalidQueryError();
    }

    return { birthday, at };
  }

  return { birthday, at: null };
}

function createSlug(birthday: Date, at: Date | null = null): string {
  let slug = "";
  if (at !== null) {
    slug += `at-${at.toLocaleString("en", { month: "long" })}-${at.getDate()}-${at.getFullYear()}-`;
  }

  return `${slug}if-born-${birthday.toLocaleString("en", { month: "long" })}-${birthday.getDate()}-${birthday.getFullYear()}`.toLowerCase();
}

function createTitleFromQuery(query: Query, locale: string, t: TFunction): string {
  let title: string;

  if (query.at !== null) {
    title = t(`${TKEY}.title_at_date`, {
      birthday: formatLongDate(query.birthday, locale),
      at: formatLongDate(query.at, locale)
    });
  } else {
    title = t(`${TKEY}.title_at_today`, {
      birthday: formatLongDate(query.birthday, locale)
    });
  }

  return title + "?";
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";

  let query: Query | null = null;

  try {
    query = getQueryFromPath((new URL(request.url)).pathname);
  } catch (e) {
    if (e instanceof InvalidQueryError) {
      throw new Response(null, {
        status: 404,
        statusText: "Not Found"
      });
    }
  }

  const t = await getTranslator(request);


  // Calculate result if query is set

  const diffMilliSeconds: number | null = query === null
    ? null
    : (query.at ?? new Date()).getTime() - query.birthday.getTime()
  ;

  const title = query ? createTitleFromQuery(query, locale, t) : t(`${TKEY}.title`);

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];


  let result: number | null = null;

  if (query !== null) {
    const slug = createSlug(query.birthday, query.at);
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${slug}` });

    result = koreanAge(query.birthday, query.at ?? new Date());
  }

  const calc = t("calculator");

  const faq = query === null ? [
    { q: t(`${TKEY}.info_h_about`), a: t(`${TKEY}.info_p_about`) },
    { q: t(`${TKEY}.info_how_it_works_h`), a: t(`${TKEY}.info_how_it_works_p`) },
    { q: t(`${TKEY}.info_when_celebrate_h`), a: t(`${TKEY}.info_when_celebrate_p`) },
  ] : [];

  return {
    query,
    result,
    diffMilliSeconds,
    faq,
    meta: {
      locale,
      title: query ? createTitleFromQuery(query, locale, t) : t(`${TKEY}.title`) + (locale === "en"
        ? " " + calc.charAt(0).toUpperCase() + calc.slice(1)
        : `, ${calc}`),
      description: query ? "" : t(`${TKEY}.description`),
      images: [`https://static.countlike.pro/korean-age-formula-${locale}.webp`],
      breadcrumbs
    }
  };
};


function koreanAge(birthDate: Date, targetDate: Date): number {
  return targetDate.getFullYear() - birthDate.getFullYear() + 1;
}

function calculate(e: FormEvent<HTMLFormElement>) {
  e.preventDefault();

  const formData = new FormData(e.currentTarget);

  // Use formData to get your values, e.g., number, unit, direction, date
  const birthday = new Date(formData.get("birthday") as string);
  const atFD = formData.get("at") as string | null;
  const at = atFD ? new Date(atFD) : null;

  // Assume you have a "createSlug" function that accepts `resultingDate` as parameter
  const slug = createSlug(birthday, at);

  // Analyze the current URL and replace or add the slug accordingly
  let currentPathname = window.location.pathname;
  const slugPattern = new RegExp(`/${ROUTE_ENDING}/?(.*)$`);

  if (slugPattern.test(currentPathname)) {
    // Replace existing slug
    currentPathname = currentPathname.replace(slugPattern, `/${ROUTE_ENDING}/${slug}`);
  } else {
    // Add slug to the pathname
    currentPathname = currentPathname.endsWith("/") ? `/${ROUTE_ENDING}/${slug}` : `/${ROUTE_ENDING}/${slug}/`;
  }

  // Redirect to the new path if it changed
  const newUrl = window.location.origin + currentPathname;
  if (window.location.href === newUrl) {
    return;
  }

  window.location.href = newUrl;
}

function getQueryFromPath(path: string): Query | null {
  const lastPathPart = path.split(`/`).pop();
  console.log(lastPathPart);
  const slug = (!lastPathPart || lastPathPart === ROUTE_ENDING)
    ? null
    : lastPathPart
  ;

  // Parse a query from the slug
  const query = slug === null ? null : parseInput(slug);
  console.debug("Parsed query", query);

  return query;
}

export default function Index() {
  const {
    meta,
    query,
    result
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);
  invariant(locale);

  const pronounciation: Record<number, string[]> = {
    1: ["한 살", "han sal"],
    2: ["두 살", "du sal"],
    3: ["세 살", "se sal"],
    4: ["네 살", "ne sal"],
    5: ["다섯 살", "da-seot sal"],
    6: ["여섯 살", "yeo-seot sal"],
    7: ["일곱 살", "il-gop sal"],
    8: ["여덟 살", "yeo-dul sal"],
    9: ["아홉 살", "a-hop sal"],
    10: ["열 살", "yeol sal"],
    11: ["열한 살", "yeol-han sal"],
    12: ["열두 살", "yeol-du sal"],
    13: ["열세 살", "yeol-se sal"],
    14: ["열네 살", "yeol-ne sal"],
    15: ["열다섯 살", "yeol-da-seot sal"],
    16: ["열여섯 살", "yeol-yeo-seot sal"],
    17: ["열일곱 살", "yeol-il-gop sal"],
    18: ["열여덟 살", "yeol-yeo-dul sal"],
    19: ["열아홉 살", "yeol-a-hop sal"],
    20: ["스무 살", "seu-mu sal"],
    21: ["스물한 살", "seu-mul-han sal"],
    22: ["스물두 살", "seu-mul-du sal"],
    23: ["스물세 살", "seu-mul-se sal"],
    24: ["스물네 살", "seu-mul-ne sal"],
    25: ["스물다섯 살", "seu-mul-da-seot sal"],
    26: ["스물여섯 살", "seu-mul-yeo-seot sal"],
    27: ["스물일곱 살", "seu-mul-il-gop sal"],
    28: ["스물여덟 살", "seu-mul-yeo-dul sal"],
    29: ["스물아홉 살", "seu-mul-a-hop sal"],
    30: ["서른 살", "seo-reun sal"],
    31: ["서른한 살", "seo-reun-han sal"],
    32: ["서른두 살", "seo-reun-du sal"],
    33: ["서른세 살", "seo-reun-se sal"],
    34: ["서른네 살", "seo-reun-ne sal"],
    35: ["서른다섯 살", "seo-reun-da-seot sal"],
    36: ["서른여섯 살", "seo-reun-yeo-seot sal"],
    37: ["서른일곱 살", "seo-reun-il-gop sal"],
    38: ["서른여덟 살", "seo-reun-yeo-dul sal"],
    39: ["서른아홉 살", "seo-reun-a-hop sal"],
    40: ["마흔 살", "ma-heun sal"],
    41: ["마흔한 살", "ma-heun-han sal"],
    42: ["마흔두 살", "ma-heun-du sal"],
    43: ["마흔세 살", "ma-heun-se sal"],
    44: ["마흔네 살", "ma-heun-ne sal"],
    45: ["마흔다섯 살", "ma-heun-da-seot sal"],
    46: ["마흔여섯 살", "ma-heun-yeo-seot sal"],
    47: ["마흔일곱 살", "ma-heun-il-gop sal"],
    48: ["마흔여덟 살", "ma-heun-yeo-dul sal"],
    49: ["마흔아홉 살", "ma-heun-a-hop sal"],
    50: ["쉰 살", "swin sal"],
    51: ["쉰한 살", "swin-han sal"],
    52: ["쉰두 살", "swin-du sal"],
    53: ["쉰세 살", "swin-se sal"],
    54: ["쉰네 살", "swin-ne sal"],
    55: ["쉰다섯 살", "swin-da-seot sal"],
    56: ["쉰여섯 살", "swin-yeo-seot sal"],
    57: ["쉰일곱 살", "swin-il-gop sal"],
    58: ["쉰여덟 살", "swin-yeo-dul sal"],
    59: ["쉰아홉 살", "swin-a-hop sal"],
    60: ["예순 살", "ye-sun sal"],
    61: ["예순한 살", "ye-sun-han sal"],
    62: ["예순두 살", "ye-sun-du sal"],
    63: ["예순세 살", "ye-sun-se sal"],
    64: ["예순네 살", "ye-sun-ne sal"],
    65: ["예순다섯 살", "ye-sun-da-seot sal"],
    66: ["예순여섯 살", "ye-sun-yeo-seot sal"],
    67: ["예순일곱 살", "ye-sun-il-gop sal"],
    68: ["예순여덟 살", "ye-sun-yeo-dul sal"],
    69: ["예순아홉 살", "ye-sun-a-hop sal"],
    70: ["일흔 살", "il-heun sal"],
    71: ["일흔한 살", "il-heun-han sal"],
    72: ["일흔두 살", "il-heun-du sal"],
    73: ["일흔세 살", "il-heun-se sal"],
    74: ["일흔네 살", "il-heun-ne sal"],
    75: ["일흔다섯 살", "il-heun-da-seot sal"],
    76: ["일흔여섯 살", "il-heun-yeo-seot sal"],
    77: ["일흔일곱 살", "il-heun-il-gop sal"],
    78: ["일흔여덟 살", "il-heun-yeo-dul sal"],
    79: ["일흔아홉 살", "il-heun-a-hop sal"],
    80: ["여든 살", "yeo-deun sal"],
    81: ["여든한 살", "yeo-deun-han sal"],
    82: ["여든두 살", "yeo-deun-du sal"],
    83: ["여든세 살", "yeo-deun-se sal"],
    84: ["여든네 살", "yeo-deun-ne sal"],
    85: ["여든다섯 살", "yeo-deun-da-seot sal"],
    86: ["여든여섯 살", "yeo-deun-yeo-seot sal"],
    87: ["여든일곱 살", "yeo-deun-il-gop sal"],
    88: ["여든여덟 살", "yeo-deun-yeo-dul sal"],
    89: ["여든아홉 살", "yeo-deun-a-hop sal"],
    90: ["아흔 살", "a-heun sal"],
    91: ["아흔한 살", "a-heun-han sal"],
    92: ["아흔두 살", "a-heun-du sal"],
    93: ["아흔세 살", "a-heun-se sal"],
    94: ["아흔네 살", "a-heun-ne sal"],
    95: ["아흔다섯 살", "a-heun-da-seot sal"],
    96: ["아흔여섯 살", "a-heun-yeo-seot sal"],
    97: ["아흔일곱 살", "a-heun-il-gop sal"],
    98: ["아흔여덟 살", "a-heun-yeo-dul sal"],
    99: ["아흔아홉 살", "a-heun-a-hop sal"],
    100: ["백 살", "baek sal"]
  };

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.title}
          description={meta.description}
        />

        {result !== null ? (
          <Result color="positive">
            <H2 className="first-letter:capitalize">{t("answer")}:</H2>

            <p className="mt-6">
              {query?.at
                ? t(`${TKEY}.result_age_for_date`, { date: formatLongDate(new Date(query.at), locale) })
                : t(`${TKEY}.result_age_for_today`)
              }

              <br /><br />
              <strong className="pt-3 text-3xl font-bold first-letter:capitalize">
                {t(`${TKEY}.result_years`, { count: result })}
              </strong>
            </p>
            <p className="mt-6">
              {t(`${TKEY}.info_pronunciation_h`)}
            </p>
            <table>
              <tbody className="flex flex-col gap-4">
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_informal`)}</strong></td>
                <td>{pronounciation[result][0]}이야. ({pronounciation[result][1]} iya.)</td>
              </tr>
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_polite`)}</strong></td>
                <td>{pronounciation[result][0]}이에요. ({pronounciation[result][1]} ieyo.)</td>
              </tr>
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_formal`)}</strong></td>
                <td>{pronounciation[result][0]}입니다. ({pronounciation[result][1]} imnida.</td>
              </tr>
              </tbody>
            </table>
          </Result>
        ) : null}

        <ToolForm onSubmit={calculate}>
          <div className="w-full 2xl:w-auto flex flex-col lg:flex-row md:items-center md:justify-start gap-4">
            <span className="font-semibold">{t(`${TKEY}.question`)}</span>
            <DatePicker
              name="birthday"
              value={query?.birthday ? new Date(query.birthday) : new Date("01-01-2000")}
              ariaLabel={t(`${TKEY}.input_date`)}
            />
          </div>
          <div className="w-full 2xl:w-auto flex flex-col lg:flex-row md:items-center md:justify-start gap-4">
            <span className="font-semibold">{t(`${TKEY}.at_date`)}</span>

            <DatePicker
              name="at"
              value={query?.at ? new Date(query.at) : null}
              ariaLabel={t(`${TKEY}.input_date`)}
              placeholder={t(`${TKEY}.result_today`)}
            />
            <span className="hidden md:inline font-semibold">?</span>
          </div>

          <Button className="w-full" isSubmit={true}>
            <Icon v="keypad" className="pr-2" /> {t("do_calculate")}
          </Button>
        </ToolForm>
      </CalculatorLayout>
      <AdditionalContentLayout>
        <AdditionalInfo>
          <AdditionalInfoSection id="about" contentsText={t(`${TKEY}.info_about_contents_text`)} contentsEmoji="ℹ️">
            <H2>{t(`${TKEY}.info_h_about`)}</H2>
            <p dangerouslySetInnerHTML={{
              __html: t(`${TKEY}.info_p_about`)
            }} />
            <p>{t(`${TKEY}.info_p2_about`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="how-it-works" contentsText={t(`${TKEY}.info_how_it_works_contents_text`)}
                                 contentsEmoji="💡">
            <H2>{t(`${TKEY}.info_how_it_works_h`)}</H2>
            <p>{t(`${TKEY}.info_how_it_works_p`)}</p>

            <ArticleImage
              src={`https://static.countlike.pro/korean-age-formula-${locale}.webp`}
              alt={t(`${TKEY}.img_caption_instructions`)}
              caption={t(`${TKEY}.img_caption_instructions`)}
              width={1200}
              height={686}
            />

            <H3>{t(`${TKEY}.info_how_it_works_option1_h`)}</H3>
            <p>{t(`${TKEY}.info_how_it_works_option1`)}</p>
            <p>{t(`${TKEY}.info_how_it_works_option1_example`)}</p>
            <H3>{t(`${TKEY}.info_how_it_works_option2_h`)}</H3>
            <p>{t(`${TKEY}.info_how_it_works_option2`)}</p>
            <p>{t(`${TKEY}.info_how_it_works_option2_example`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="bts" contentsText={t(`${TKEY}.info_bts_info_contents_text`)} contentsEmoji="🎤">
            <H2>{t(`${TKEY}.info_bts_h`)}</H2>
            <p dangerouslySetInnerHTML={{
              __html: t(`${TKEY}.info_bts_p`)
            }} />
            <ul className="list-disc list-inside">
              <li>{t(`${TKEY}.info_bts_1`)} - {new Date(1994, 8, 12).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1994, 8, 12), new Date())
              })
              }</li>
              <li>{t(`${TKEY}.info_bts_2`)} - {new Date(1993, 2, 9).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1993, 2, 9), new Date())
              })
              }</li>
              <li>{t(`${TKEY}.info_bts_3`)} - {new Date(1994, 1, 18).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1994, 1, 18), new Date())
              })
              }</li>
              <li>{t(`${TKEY}.info_bts_4`)} - {new Date(1995, 11, 30).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1995, 11, 30), new Date())
              })
              }</li>
              <li>{t(`${TKEY}.info_bts_5`)} - {new Date(1995, 9, 13).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1995, 9, 13), new Date())
              })
              }</li>
              <li>{t(`${TKEY}.info_bts_6`)} - {new Date(1992, 11, 4).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1992, 11, 4), new Date())
              })
              }</li>
              <li>{t(`${TKEY}.info_bts_7`)} - {new Date(1997, 8, 1).toLocaleDateString(locale)} - {t(`${TKEY}.result_years`, {
                count: koreanAge(new Date(1997, 8, 1), new Date())
              })
              }</li>
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="celebrations" contentsText={t(`${TKEY}.info_celebrations_contents_text`)}
                                 contentsEmoji="🎉">
            <H2>{t(`${TKEY}.info_when_celebrate_h`)}</H2>
            <p>{t(`${TKEY}.info_when_celebrate_p`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="pronunciation" contentsText={t(`${TKEY}.info_pronunciation_contents_text`)}
                                 contentsEmoji="🗣️">
            <H2>{t(`${TKEY}.info_pronunciation_h`)}</H2>
            <p>{t(`${TKEY}.info_pronunciation_p`)}</p>

            <H3>{t(`${TKEY}.info_pronunciation_question`)}</H3>
            <table className="table-auto mb-6">
              <tbody className="flex flex-col gap-4">
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_informal`)}</strong></td>
                <td>몇 살이야? (meot sariya?)</td>
              </tr>
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_formal`)}</strong></td>
                <td>연세가 어떻게 되세요? (yeonsega eotteoke doeseyo?)</td>
              </tr>
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_polite`)}</strong></td>
                <td>나이가 어떻게 돼요? (naiga eotteoke doeyo?)</td>
              </tr>
              </tbody>
            </table>
            <BlockQuote className="mb-6">
              실례지만 나이가 어떻게 되세요? <br />
              Sillyejiman naiga eotteoke doeseyo? <br />
              {t(`${TKEY}.info_pronunciation_excuse_me`)}
            </BlockQuote>


            {result === null ? null : (
              <>
                <H3>{t(`${TKEY}.info_pronunciation_your_calculated_age`)}</H3>
                <p>{t(`${TKEY}.result_years`, { count: result })}</p>
                <table>
                  <tbody className="flex flex-col gap-4 mb-6">
                  <tr className="flex flex-col md:flex-row gap-2">
                    <td><strong>{t(`${TKEY}.info_pronunciation_informal`)}</strong></td>
                    <td>{pronounciation[result][0]}이야. ({pronounciation[result][1]} iya.)</td>
                  </tr>
                  <tr className="flex flex-col md:flex-row gap-2">
                    <td><strong>{t(`${TKEY}.info_pronunciation_formal`)}</strong></td>
                    <td>{pronounciation[result][0]}입니다. ({pronounciation[result][1]} imnida.</td>
                  </tr>
                  <tr className="flex flex-col md:flex-row gap-2">
                    <td><strong>{t(`${TKEY}.info_pronunciation_polite`)}</strong></td>
                    <td>{pronounciation[result][0]}이에요. ({pronounciation[result][1]} ieyo.)</td>
                  </tr>
                  </tbody>
                </table>
              </>
            )}
            <H3>{t(`${TKEY}.info_pronunciation_answer`)}</H3>
            <table className="table-auto mb-6">
              <tbody className="flex flex-col gap-4">
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_informal`)}</strong></td>
                <td>___이야. (___ iya.)</td>
              </tr>
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_formal`)}</strong></td>
                <td>___입니다. (___ imnida.</td>
              </tr>
              <tr className="flex flex-col md:flex-row gap-2">
                <td><strong>{t(`${TKEY}.info_pronunciation_polite`)}</strong></td>
                <td>___이에요. (___ ieyo.)</td>
              </tr>
              </tbody>
            </table>

            <H3>{t(`${TKEY}.info_pronunciation_other_ages`)}</H3>
            <table className="table table-auto w-full">
              <thead>
              <tr className="text-left">
                <th><strong>Age</strong></th>
                <th><strong>Korean</strong></th>
                <th><strong>Romanization</strong></th>
              </tr>
              </thead>
              <tbody>
              {Object.entries(pronounciation).map(([age, pron]) => (
                <tr key={age}>
                  <td>
                    {t(`${TKEY}.info_pronunciation_x_years`, { count: parseInt(age) })} <br />
                  </td>

                  <td><strong>{pron[0]}</strong></td>
                  <td><em>{pron[1]}</em></td>
                </tr>
              ))}
              </tbody>
            </table>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="international" contentsText={t(`${TKEY}.info_international_contents_text`)}
                                 contentsEmoji="🌍">
            <H2>{t(`${TKEY}.info_switch_to_international_h`)}</H2>
            <p>{t(`${TKEY}.info_switch_to_international_p`)}</p>
            <BlockQuote>
              {t(`${TKEY}.info_switch_to_international_quote`)}
            </BlockQuote>
          </AdditionalInfoSection>
        </AdditionalInfo>

        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>
    </>
  );
}


